// ./src/pages/HooksExtractorPage.js
import React, { useState, useRef, useMemo } from 'react';
import { Search, Copy, Check, AlertCircle, Loader, AlertTriangle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useOpenGraph from '../hooks/useOpenGraph';

const HooksExtractorPage = () => {
  const [searchParams] = useSearchParams();
  const limitsDisabled = searchParams.get('limits') === 'off';
  useDocumentTitle('WordPress Hooks Extractor');
  useOpenGraph({
    title: 'WordPress Hooks Extractor',
    description: 'Quickly scan any plugin or theme for action and filter hooks to see what\'s available for extending or customizing. Save time identifying relevant hooks.',
  });
  
  const [pluginSlug, setPluginSlug] = useState('');
  const [type, setType] = useState('plugin');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [hooksData, setHooksData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [copied, setCopied] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const textareaRef = useRef(null);
  // New state to toggle between input modes
  const [inputMode, setInputMode] = useState('slug'); // 'slug' or 'upload'

  // New settings states
  const [contextBefore, setContextBefore] = useState(3);
  const [contextAfter, setContextAfter] = useState(3);
  const [showSettings, setShowSettings] = useState(false);
  
  // New state for sidebar visibility and hook toggling
  const [showSidebar, setShowSidebar] = useState(false);
  const [visibleHooks, setVisibleHooks] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const [filterText, setFilterText] = useState('');

  const handleClearFile = () => {
    setUploadedFile(null);
    const fileInput = document.getElementById('zip-upload');
    if (fileInput) fileInput.value = '';
  };

  // Toggle to upload mode
  const switchToUploadMode = () => {
    setInputMode('upload');
    setPluginSlug('');
  };

  // Toggle to slug mode and clear any uploaded file
  const switchToSlugMode = () => {
    setInputMode('slug');
    handleClearFile();
  };

  const extractHooks = async (e) => {
    e.preventDefault();
    if (!pluginSlug.trim() && !uploadedFile) {
      setError(`Please enter a ${type} slug or upload a ZIP file`);
      return;
    }
    
    setIsLoading(true);
    setError('');
    setHooksData([]);
    setWarnings([]);
    setHasSearched(false);
  
    const worker = new Worker(`${process.env.PUBLIC_URL}/workers/hooksWorker.min.js`);
    
    if (uploadedFile) {
      try {
        const buffer = await uploadedFile.arrayBuffer();
        worker.postMessage({ 
          file: buffer, 
          contextBefore, 
          contextAfter,
          limitsDisabled 
        }, [buffer]);
      } catch (err) {
        setError('Failed to read the uploaded file.');
        setIsLoading(false);
        return;
      }
    } else {
      worker.postMessage({ 
        slug: pluginSlug, 
        type, 
        contextBefore, 
        contextAfter,
        limitsDisabled 
      });
    }
  
    worker.onmessage = (e) => {
      const { results, error, warnings, status } = e.data;
    
      if (status) {
        setStatusMessage(status);
        return;
      }
    
      if (error) setError(error);
      else {
        setHooksData(results || []);
        // Initialize all hooks as visible (keyed by index)
        if (results && results.length) {
          const initialVisibility = {};
          results.forEach((hook, index) => {
            initialVisibility[index] = true;
          });
          setVisibleHooks(initialVisibility);
        }
      }
      if (warnings) setWarnings(warnings);
      setIsLoading(false);
      setHasSearched(true);
      setStatusMessage('');
      worker.terminate();
    };
  };  

  const copyToClipboard = () => {
    if (textareaRef.current) {
      navigator.clipboard.writeText(textareaRef.current.value)
        .then(() => {
          setCopied(true);
          setTimeout(() => {
            setCopied(false);
          }, 2000);
        })
        .catch(() => {
          setError('Failed to copy to clipboard');
        });
    }
  };

  // Compute markdown and record the starting index (character position) of each hook’s section
  const computeMarkdownAndPositions = () => {
    let markdown = '';
    const positions = {};
    const types = ['filter', 'action'];
    
    types.forEach((hookType) => {
      // Filter hooks by type and visibility
      const hooksOfType = hooksData
        .map((hook, index) => ({ ...hook, index }))
        .filter(item => item.type === hookType && visibleHooks[item.index]);
      
      if (hooksOfType.length) {
        markdown += `## ${hookType === 'filter' ? 'Filters' : 'Actions'} (${hooksOfType.length})\n\n`;
        hooksOfType.forEach(item => {
          // Record the position for this hook
          positions[item.index] = markdown.length;
          markdown += `### ${item.name}\n`;
          markdown += `**File:** \`${item.file}\`\n\n`;
          markdown += `**Context:**\n\n`;
          markdown += "```php\n";
          markdown += item.context.trim();
          markdown += "\n```\n\n";
        });
      }
    });
    
    return { markdown, positions };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { markdown, positions } = useMemo(() => computeMarkdownAndPositions(), [hooksData, visibleHooks]);

  // Scroll the textarea to the hook's section based on computed position
  const scrollToHook = (index) => {
    const pos = positions[index];
    if (textareaRef.current && pos !== undefined) {
      // Set the selection to the hook's starting index
      textareaRef.current.setSelectionRange(pos, pos);
      textareaRef.current.focus();
    }
  };

  // Toggle all hook checkboxes (modified to respect filtering)
  const allChecked = hooksData.length > 0 && 
    hooksData
      .filter(hook => !filterText || hook.name.toLowerCase().includes(filterText.toLowerCase()))
      .length > 0 && // ensure there are filtered hooks
    hooksData
      .filter(hook => !filterText || hook.name.toLowerCase().includes(filterText.toLowerCase()))
      .every((hook, i) => visibleHooks[hooksData.indexOf(hook)]);

  const toggleAllHooks = () => {
    const newState = { ...visibleHooks };
    const filtered = hooksData.filter(hook => !filterText || hook.name.toLowerCase().includes(filterText.toLowerCase()));
    
    // Determine the new state based on currently filtered hooks
    const newValue = !filtered.every((hook) => visibleHooks[hooksData.indexOf(hook)]);
    
    // Only update state for filtered hooks
    filtered.forEach(hook => {
      const index = hooksData.indexOf(hook);
      newState[index] = newValue;
    });
    
    setVisibleHooks(newState);
  };

  const toggleFilter = () => {
    setShowFilter(!showFilter);
    if (showFilter) {  // will hide filter
      setFilterText('');
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-6 text-center text-gray-900">
          WordPress 
          <span className="text-blue-600 ml-2">Hooks Extractor</span>
        </h1>
        
        <div className="bg-white rounded-lg shadow-md p-8 mb-10">
          <p className="text-gray-700 mb-6">
            Extract action and filter hooks from any WordPress plugin or theme. Results are provided in Markdown format, 
            ready to use with ChatGPT or other AI tools for customization assistance.
          </p>
          
          <form onSubmit={extractHooks} className="mb-8">
            {inputMode === 'slug' ? (
              <div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-[150px] md:pr-4 mb-4 md:mb-0">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Extract from:
                      </label>
                      <div className="flex flex-col space-y-2">
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            name="extension-type"
                            value="plugin"
                            checked={type === 'plugin'}
                            onChange={() => setType('plugin')}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="ml-2">Plugin</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            name="extension-type"
                            value="theme"
                            checked={type === 'theme'}
                            onChange={() => setType('theme')}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="ml-2">Theme</span>
                        </label>
                      </div>
                    </div>
                    
                    <div className="md:border-l md:border-gray-200"></div>
                    
                    <div className="md:flex-1 md:pl-4">
                      <div className="flex justify-between items-center mb-2">
                        <label htmlFor="plugin-slug" className="block text-sm font-medium text-gray-700">
                          WordPress.org {type} slug:
                        </label>
                        <button 
                          type="button"
                          onClick={switchToUploadMode}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          Upload a ZIP file instead
                        </button>
                      </div>
                      <input
                        id="plugin-slug"
                        type="text"
                        value={pluginSlug}
                        onChange={(e) => setPluginSlug(e.target.value)}
                        placeholder={`e.g. ${type === 'plugin' ? 'classic-editor' : 'generatepress'}`}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-medium text-gray-800">Upload a plugin or theme zip file</h3>
                    <button 
                      type="button"
                      onClick={switchToSlugMode}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Use WordPress.org slug instead
                    </button>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <input
                        id="zip-upload"
                        type="file"
                        accept=".zip"
                        onChange={(e) => setUploadedFile(e.target.files[0] || null)}
                        className="flex-1"
                      />
                      {uploadedFile && (
                        <button 
                          type="button"
                          onClick={handleClearFile}
                          className="text-sm text-red-600 hover:text-red-700 font-medium whitespace-nowrap"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mt-6 flex justify-center">
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2 mr-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center disabled:bg-blue-400 min-w-[150px]"
              >
                {isLoading ? (
                  <><Loader className="w-5 h-5 mr-2 animate-spin" /> Extracting...</>
                ) : (
                  <><Search className="w-5 h-5 mr-2" /> Extract Hooks</>
                )}
              </button>

              <button 
                type="button"
                onClick={() => setShowSettings(!showSettings)} 
                className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center"
              >
                <span>{showSettings ? 'Hide' : 'Show'} advanced settings</span>
                <span className="ml-1">{showSettings ? '▲' : '▼'}</span>
              </button>
            </div>

            
            {showSettings && (
              <div className="mt-6 mb-2 text-center">
                <div className="mt-3 p-3 bg-gray-50 rounded-lg mx-auto max-w-md">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Context lines before
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="20"
                        value={contextBefore}
                        onChange={(e) => setContextBefore(parseInt(e.target.value) || 0)}
                        className="mt-1 w-full px-2 py-1 border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Context lines after
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="20"
                        value={contextAfter}
                        onChange={(e) => setContextAfter(parseInt(e.target.value) || 0)}
                        className="mt-1 w-full px-2 py-1 border border-gray-300 rounded"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    These settings control how many lines of code before and after the hook are included in the results. 
                    Comments and docblocks directly above the hooks are always included.
                  </p>
                </div>
              </div>
            )}
            
            {error && (
              <div className="mt-2">
                <div className="text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {error}
                </div>
              </div>
            )}
          </form>

          {warnings && warnings.length > 0 && (
            <div className="mb-6 border border-yellow-300 bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" />
                <h3 className="font-medium">Warnings</h3>
              </div>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
              <div className="mt-3 text-sm text-gray-700 p-2 bg-orange-100 rounded">
                These warnings are related to limitations of this free service. For complete hook extraction without constraints, 
                you can <a href="https://wp-autoplugin.com/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                  download WP-Autoplugin</a> and run it on your own WordPress site.
              </div>
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center items-center py-12 flex-col text-center">
              <Loader className="w-8 h-8 text-blue-600 animate-spin mb-3" />
              <span className="text-lg text-gray-700">
                {statusMessage || 'Extracting hooks, please wait...'}
              </span>
            </div>
          )}
          
          {!isLoading && hooksData.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-xl font-semibold">Results</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={copyToClipboard}
                    className="flex items-center px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    {copied ? (
                      <><Check className="w-4 h-4 mr-1 text-green-600" /> Copied!</>
                    ) : (
                      <><Copy className="w-4 h-4 mr-1" /> Copy Markdown</>
                    )}
                  </button>
                  <button
                    onClick={() => setShowSidebar(!showSidebar)}
                    className="flex items-center px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    {showSidebar ? 'Hide List' : 'Show List'}
                  </button>
                </div>
              </div>
              
              {showSidebar ? (
                <div className="lg:grid lg:grid-cols-[1fr,16rem] lg:gap-4 flex flex-col space-y-4 lg:space-y-0">
                  <textarea
                    ref={textareaRef}
                    readOnly
                    value={markdown}
                    className="w-full h-96 p-4 font-mono text-sm border border-gray-300 rounded-lg bg-gray-50"
                  />
                  <div className="border border-gray-200 p-2 overflow-auto h-96">
                    <div className="flex items-center gap-4 mb-2">
                      <button
                        onClick={toggleFilter}
                        className="text-xs text-gray-600 hover:text-blue-600"
                      >
                        Filter
                      </button>
                      <div className="flex-1 text-center">
                        <span className="font-medium">
                          Hooks ({hooksData.filter(hook => 
                            (!filterText || hook.name.toLowerCase().includes(filterText.toLowerCase()))
                          ).length})
                        </span>
                      </div>
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={toggleAllHooks}
                        className="h-4 w-4"
                      />
                    </div>
                    {showFilter && (
                      <div className="mb-2">
                        <input
                          type="text"
                          value={filterText}
                          onChange={(e) => setFilterText(e.target.value)}
                          placeholder="Filter hooks..."
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      {hooksData
                        .map((hook, originalIndex) => ({ ...hook, originalIndex }))
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(({ originalIndex, ...hook }) => (
                        (!filterText || hook.name.toLowerCase().includes(filterText.toLowerCase())) && (
                          <div key={originalIndex} className="flex items-center justify-between py-1 border-b border-gray-100">
                            <div className="flex items-center w-full">
                              <input
                                type="checkbox"
                                checked={visibleHooks[originalIndex] || false}
                                onChange={(e) => setVisibleHooks(prev => ({ ...prev, [originalIndex]: e.target.checked }))}
                                className="mr-2 flex-shrink-0"
                              />
                              <button 
                                type="button" 
                                onClick={() => scrollToHook(originalIndex)}
                                className="text-sm font-mono break-all hover:text-blue-600 text-left"
                              >
                                {hook.name}
                              </button>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <textarea
                  ref={textareaRef}
                  readOnly
                  value={markdown}
                  className="w-full h-96 p-4 font-mono text-sm border border-gray-300 rounded-lg bg-gray-50"
                />
              )}
              
              <div className="mt-4 text-gray-600 text-sm">
                <p>Found {hooksData.length} hooks in total:</p>
                <ul className="list-disc list-inside mt-1">
                  <li>{hooksData.filter(hook => hook.type === 'filter').length} filters</li>
                  <li>{hooksData.filter(hook => hook.type === 'action').length} actions</li>
                </ul>
              </div>
            </div>
          )}
          
          {!isLoading && !error && hooksData.length === 0 && hasSearched && (
            <div className="text-center py-8 text-gray-500">
              No hooks found. Try another {type} or check the {type} slug.
            </div>
          )}
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-8 mb-10">
          <h2 className="text-2xl font-semibold mb-4">About This Tool</h2>
          <p className="text-gray-700 mb-4">
            The WordPress Hooks Extractor identifies customization points in WordPress plugins and themes by scanning their 
            source code for action and filter hooks. It only analyzes PHP files and detects standard WordPress hook functions like 
            <code className="bg-gray-100 px-1 py-0.5 rounded">apply_filters</code>, 
            <code className="bg-gray-100 px-1 py-0.5 rounded">do_action</code>, and 
            <code className="bg-gray-100 px-1 py-0.5 rounded">do_action_ref_array</code>.
          </p>
          <p className="text-gray-700 mb-4">
            Key benefits:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
            <li>Safely customize behavior without modifying core files</li>
            <li>Find integration points for building extensions</li>
            <li>Understand internal hook architecture</li>
          </ul>
          <p className="text-gray-700">
            The Markdown output format makes it easy to analyze hooks with AI tools and start building your customizations.
          </p>

          <p className="text-gray-700 mt-6">
            This tool runs entirely in your browser. When you use the WordPress.org slug option, plugin/theme files are downloaded directly 
            to your browser. When you use the file upload option, your ZIP file is processed locally - it's never uploaded to any server.
          </p>

          <p className="text-gray-700 mt-6 border-l-4 border-blue-400 px-3 py-2 bg-blue-50">
            While this tool is free and fast, it does have a few built-in constraints. If you ever run into any limitations here, you can download and use 
            <a href="https://wp-autoplugin.com/" className="text-blue-600 hover:underline ml-1 mr-1" target="_blank" rel="noopener noreferrer">
              WP-Autoplugin
            </a> 
            on your own site to extract hooks without these constraints.
          </p>
        </div>

      </div>
    </div>
  );
};

export default HooksExtractorPage;
